import type React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, RefreshCw, RotateCw, Sparkles, X } from 'lucide-react';
import type { GenerationScope, Row } from '@tekvoice/shared';
import { useReview } from '@/components/review/review-context';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { api } from '@/lib/api';
import { isApprovable, isFailed, isMissing, rowAssets } from '@/lib/assets';

interface BulkBarProps {
  selectedRows: Row[];
  onClear: () => void;
  onRequestGenerate: (scope: GenerationScope, label: string) => void;
  canGenerate: boolean;
  canApprove: boolean;
}

/** SPEC §11 — Generate missing / Retry failed / Approve selected / Regenerate selected. */
export function BulkBar({
  selectedRows,
  onClear,
  onRequestGenerate,
  canGenerate,
  canApprove,
}: BulkBarProps): React.JSX.Element | null {
  const { batchId, invalidate } = useReview();

  const approveBulk = useMutation({
    mutationFn: (rowIds: string[]) => api.batches.approveBulk(batchId, rowIds),
    onSuccess: (result) => {
      if (result.approved > 0) toast.success(`Approved ${result.approved} row(s)`);
      const skipped = result.outcomes.filter((outcome) => !outcome.approved);
      for (const outcome of skipped.slice(0, 5)) {
        toast.error(
          `Row ${outcome.rowIndex + 1} skipped — ${outcome.reasons.map((reason) => reason.replace(/_/g, ' ')).join(', ')}`,
        );
      }
      if (skipped.length > 5) toast.error(`…and ${skipped.length - 5} more row(s) skipped`);
      onClear();
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (selectedRows.length === 0) return null;

  const missingRows = selectedRows.filter((row) =>
    rowAssets(row).some((asset) => isMissing(row, asset)),
  );
  const failedRows = selectedRows.filter((row) => rowAssets(row).some(isFailed));
  const approvableRows = selectedRows.filter(isApprovable);
  const regenerableRows = selectedRows.filter((row) =>
    rowAssets(row).some((asset) => asset.currentTakeId !== null),
  );

  return (
    <div
      role="toolbar"
      aria-label="Bulk actions"
      data-testid="bulk-bar"
      className="sticky bottom-4 z-20 mx-auto flex w-fit max-w-full flex-wrap items-center gap-2 rounded-full border bg-background/95 px-4 py-2 shadow-lg backdrop-blur"
    >
      <span className="text-sm font-medium">{selectedRows.length} selected</span>

      {/*
        C2 — every generate button carries its INTENT alongside the row ids. A bare
        `{rowIds}` scope means "every asset in these rows", so "Retry failed (1)" on a row
        whose other language is APPROVED used to regenerate that language too: its
        `currentTakeId` moved off `approvedTakeId`, the status dropped to GENERATED and the
        next export emitted an empty URL cell for a clip still live in the bucket.
      */}
      <Button
        size="sm"
        variant="outline"
        disabled={!canGenerate || missingRows.length === 0}
        onClick={() =>
          onRequestGenerate(
            { rowIds: missingRows.map((row) => row.id), only: 'missing' },
            `missing audio in ${missingRows.length} row(s)`,
          )
        }
      >
        <Sparkles className="h-3.5 w-3.5" />
        Generate missing ({missingRows.length})
      </Button>

      <Button
        size="sm"
        variant="outline"
        disabled={!canGenerate || failedRows.length === 0}
        onClick={() =>
          onRequestGenerate(
            { rowIds: failedRows.map((row) => row.id), only: 'failed' },
            `retry ${failedRows.length} failed row(s)`,
          )
        }
      >
        <RotateCw className="h-3.5 w-3.5" />
        Retry failed ({failedRows.length})
      </Button>

      {/* The only button that deliberately regenerates everything in the selection. */}
      <Button
        size="sm"
        variant="outline"
        disabled={!canGenerate || regenerableRows.length === 0}
        onClick={() =>
          onRequestGenerate(
            { rowIds: regenerableRows.map((row) => row.id) },
            `regenerate ${regenerableRows.length} row(s)`,
          )
        }
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Regenerate selected ({regenerableRows.length})
      </Button>

      <Button
        size="sm"
        disabled={!canApprove || approvableRows.length === 0 || approveBulk.isPending}
        onClick={() => approveBulk.mutate(selectedRows.map((row) => row.id))}
      >
        <Check className="h-3.5 w-3.5" />
        {approveBulk.isPending ? 'Approving…' : `Approve selected (${approvableRows.length})`}
      </Button>

      <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Clear selection" onClick={onClear}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
